/**
 * Config lingotti d'oro singoli per /lingotti-oro/:slug
 * Target SEO: "quanto vale lingotto oro 5g", "prezzo lingotto 1kg", ecc.
 */

export interface LingottoOro {
  slug: string;
  nome: string;          // Es. "Lingotto oro 5g"
  titleTag: string;
  metaDesc: string;
  h1: string;
  pesoGrammi: number;    // grammi oro fino (lingotti sono sempre 999,9‰)
  formato: 'minted' | 'cast'; // coniato vs fuso
  intro: string;
  premiumInfo: string;
  faqs: Array<{ q: string; a: string }>;
}

const oncia = 31.1035;

export const LINGOTTI_ORO: Record<string, LingottoOro> = {
  '1g': {
    slug: '1g',
    nome: 'Lingotto oro 1 grammo',
    titleTag: 'Quotazione Lingotto Oro 1g Oggi | Prezzo Aggiornato | Gildy',
    metaDesc:
      'Quotazione lingotto oro 1 grammo aggiornata. Premium di conio, dove comprarlo e venderlo. Il taglio ideale per chi inizia con l\'oro fisico.',
    h1: 'Quanto vale un lingotto d\'oro da 1 grammo',
    pesoGrammi: 1,
    formato: 'minted',
    intro:
      'Il lingotto da 1 grammo è il taglio più piccolo prodotto dalle raffinerie LBMA (PAMP, Valcambi, Argor-Heraeus). È in oro puro 999,9‰ (24kt), coniato e sigillato in blister con certificato di autenticità. Ideale come prima entry nell\'oro fisico o come regalo.',
    premiumInfo:
      'Il premium sul lingotto da 1g è il più alto della categoria: tipicamente 15-30% sul valore spot, per via dei costi fissi di conio e packaging. Per accumulo conviene salire a tagli da 5g o 10g.',
    faqs: [
      {
        q: 'Conviene il lingotto da 1g o una sterlina?',
        a: 'A parità di spesa la sterlina (7,32g oro fino) ha premium percentuale molto più basso (3-6% vs 15-30%). Il lingotto da 1g si giustifica solo come regalo o entry simbolica.',
      },
      {
        q: 'Si può rivendere facilmente?',
        a: 'Sì, ma con perdita: lo spread di rivendita su un 1g può azzerare il premium pagato all\'acquisto. Conserva il blister originale per massimizzare il prezzo.',
      },
    ],
  },
  '5g': {
    slug: '5g',
    nome: 'Lingotto oro 5 grammi',
    titleTag: 'Quotazione Lingotto Oro 5g Oggi | Prezzo Aggiornato | Gildy',
    metaDesc:
      'Quotazione lingotto oro 5 grammi aggiornata. Premium contenuto, taglio popolare per accumulo. Dove venderlo al miglior prezzo.',
    h1: 'Quanto vale un lingotto d\'oro da 5 grammi',
    pesoGrammi: 5,
    formato: 'minted',
    intro:
      'Il lingotto da 5g è uno dei tagli più venduti in Italia per accumulo graduale. Prodotto in oro 999,9‰ coniato (PAMP, Valcambi), sigillato in blister con certificato. Pesa esattamente 5g di oro fino.',
    premiumInfo:
      'Premium tipico 7-12% sul valore spot per lingotti in blister originale. La rivendita ai compro oro applica spread del 3-6% per lingotti LBMA certificati.',
    faqs: [
      {
        q: 'Va bene per accumulo mensile?',
        a: 'Sì, è il taglio più razionale per acquisti mensili da 300-500€. Premium accettabile e liquidità alta sul mercato secondario.',
      },
    ],
  },
  '10g': {
    slug: '10g',
    nome: 'Lingotto oro 10 grammi',
    titleTag: 'Quotazione Lingotto Oro 10g Oggi | Prezzo Aggiornato | Gildy',
    metaDesc:
      'Quotazione lingotto oro 10 grammi aggiornata. Taglio ottimale per accumulo serio, premium 5-8% e ottima liquidità di rivendita.',
    h1: 'Quanto vale un lingotto d\'oro da 10 grammi',
    pesoGrammi: 10,
    formato: 'minted',
    intro:
      'Il lingotto da 10g è considerato il taglio "sweet spot" tra premium contenuto e accessibilità. Oro 999,9‰ coniato in blister con certificato LBMA. Pesa 10g esatti di oro fino.',
    premiumInfo:
      'Premium tipico 5-8% sul valore spot. Rivendita a compro oro con spread 2-4% per lingotti certificati in blister integro.',
    faqs: [
      {
        q: '10g è meglio di 2 lingotti da 5g?',
        a: 'Sì, il premium è leggermente più basso (5-8% vs 7-12%). Lo svantaggio è la minore frazionabilità in caso di vendita parziale.',
      },
    ],
  },
  '20g': {
    slug: '20g',
    nome: 'Lingotto oro 20 grammi',
    titleTag: 'Quotazione Lingotto Oro 20g Oggi | Prezzo Aggiornato | Gildy',
    metaDesc:
      'Quotazione lingotto oro 20 grammi aggiornata. Premium 4-6%, taglio efficiente per investimento di medio importo. Liquidità ottima.',
    h1: 'Quanto vale un lingotto d\'oro da 20 grammi',
    pesoGrammi: 20,
    formato: 'minted',
    intro:
      'Il lingotto da 20g è un taglio meno comune ma molto efficiente: premium più basso del 10g e ancora abbastanza frazionabile in caso di rivendita. Oro 999,9‰ coniato, blister con certificato.',
    premiumInfo:
      'Premium tipico 4-6% sul valore spot. Spread di rivendita 2-3% per lingotti LBMA certificati.',
    faqs: [
      {
        q: 'È un taglio standard?',
        a: 'Sì, prodotto da PAMP e Valcambi. Non tutti i compro oro lo trattano: verifica prima della vendita.',
      },
    ],
  },
  '50g': {
    slug: '50g',
    nome: 'Lingotto oro 50 grammi',
    titleTag: 'Quotazione Lingotto Oro 50g Oggi | Prezzo Aggiornato | Gildy',
    metaDesc:
      'Quotazione lingotto oro 50 grammi aggiornata. Premium 3-5%, taglio popolare per investimento serio. Cassetta sicurezza consigliata.',
    h1: 'Quanto vale un lingotto d\'oro da 50 grammi',
    pesoGrammi: 50,
    formato: 'minted',
    intro:
      'Il lingotto da 50g è un taglio di riferimento per investitori di medio livello. Premium contenuto (3-5%) e ottima liquidità presso banche e dealer professionali. Oro 999,9‰ coniato in blister LBMA con certificato di autenticità.',
    premiumInfo:
      'Premium tipico 3-5% sul valore spot. Rivendita a dealer con spread 1,5-3% per lingotti LBMA certificati in blister integro.',
    faqs: [
      {
        q: 'Dove conservarlo?',
        a: 'A oltre 4.000€ di valore (al 2026) si consiglia cassetta di sicurezza bancaria o caveau dedicato. L\'assicurazione casa copre raramente oltre i 2.000€ di oro fisico.',
      },
    ],
  },
  '100g': {
    slug: '100g',
    nome: 'Lingotto oro 100 grammi',
    titleTag: 'Quotazione Lingotto Oro 100g Oggi | Prezzo Aggiornato | Gildy',
    metaDesc:
      'Quotazione lingotto oro 100 grammi aggiornata. Premium 2-4%, taglio professionale per portafogli importanti. Mercato istituzionale.',
    h1: 'Quanto vale un lingotto d\'oro da 100 grammi',
    pesoGrammi: 100,
    formato: 'minted',
    intro:
      'Il lingotto da 100g è il taglio "professionale" per portafogli importanti: premium molto contenuto e accesso al mercato istituzionale (banche, fondi). Oro 999,9‰ coniato in blister LBMA o fuso con marchio raffineria.',
    premiumInfo:
      'Premium tipico 2-4% sul valore spot per lingotti coniati, 1-2% per lingotti fusi. Spread di rivendita 1-2% per pezzi LBMA certificati.',
    faqs: [
      {
        q: 'Coniato o fuso?',
        a: 'Il coniato (minted) ha estetica migliore e blister sigillato, premium 2-4%. Il fuso (cast) ha premium più basso (1-2%) ma packaging minimale. Stessa purezza 999,9‰.',
      },
    ],
  },
  '1oz': {
    slug: '1oz',
    nome: 'Lingotto oro 1 oncia (31,1g)',
    titleTag: 'Quotazione Lingotto Oro 1 Oncia 31g | Gildy',
    metaDesc:
      'Quotazione lingotto oro 1 oncia troy (31,1035g) aggiornata. Lo standard internazionale, premium 3-5%, liquidità globale.',
    h1: 'Quanto vale un lingotto d\'oro da 1 oncia',
    pesoGrammi: oncia,
    formato: 'minted',
    intro:
      'Il lingotto da 1 oncia troy (31,1035g) è lo standard internazionale per il commercio dell\'oro fisico. Stesso peso oro fino di Krugerrand, Eagle e Maple Leaf, ma in formato lingotto. Oro 999,9‰ puro coniato, accettato da qualsiasi dealer al mondo.',
    premiumInfo:
      'Premium tipico 3-5% sul valore spot per lingotti LBMA in blister originale. Liquidità globale: rivendibile praticamente ovunque al prezzo spot meno 1-2% di spread.',
    faqs: [
      {
        q: 'Differenza con il Krugerrand?',
        a: 'Stesso peso oro (31,1035g) ma il lingotto è oro puro 24kt mentre il Krugerrand è 22kt (con rame). Premium e liquidità simili.',
      },
    ],
  },
  '1kg': {
    slug: '1kg',
    nome: 'Lingotto oro 1 kg',
    titleTag: 'Quotazione Lingotto Oro 1 Kg Oggi | Prezzo Aggiornato | Gildy',
    metaDesc:
      'Quotazione lingotto oro 1 kg aggiornata. Premium minimo 1-2%, formato istituzionale. Investimento serio: cassetta o caveau obbligatori.',
    h1: 'Quanto vale un lingotto d\'oro da 1 kg',
    pesoGrammi: 1000,
    formato: 'cast',
    intro:
      'Il lingotto da 1 kg è il formato istituzionale per eccellenza: premium minimo, accesso diretto al mercato wholesale LBMA. Oro 999,9‰ fuso con marchio raffineria (PAMP, Valcambi, Argor-Heraeus, Metalor), numero seriale univoco e certificato.',
    premiumInfo:
      'Premium tipico 1-2% sul valore spot per lingotti LBMA fusi. Rivendita a dealer professionali con spread 0,5-1,5%. Valori oltre 80.000€ (al 2026): cassetta di sicurezza bancaria o caveau dedicato obbligatori.',
    faqs: [
      {
        q: 'Chi compra un lingotto da 1kg?',
        a: 'Dealer professionali, banche di investimento, family office, raffinerie. Il mercato retail tratta tagli fino a 100g/1oz.',
      },
      {
        q: 'Va sempre periziato?',
        a: 'Per lingotti LBMA con numero seriale e certificato originale basta verifica documentale. Per pezzi senza tracciabilità è obbligatoria perizia XRF e ultrasuoni.',
      },
    ],
  },
};

export const LINGOTTI_SLUGS = Object.keys(LINGOTTI_ORO);
export const isValidLingotto = (s?: string): boolean => !!s && s in LINGOTTI_ORO;
export const getLingotto = (s: string): LingottoOro | undefined => LINGOTTI_ORO[s];
